import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const sections = [
  {
    title: 'Introduction',
    content: [
      {
        text: 'This Privacy Policy explains how Reano AI Technologies Private Limited ("Reano AI", "we", "our") collects, uses, stores, and protects your information when you use our AI chatbot platform ("Service").',
      },
      {
        text: 'By using our Service, you agree to this Privacy Policy.',
      },
    ],
  },
  {
    title: 'Information We Collect or May Collect',
    content: [
      {
        subtitle: 'a. Account information',
        list: ['Name', 'Email address', 'Phone number', 'Business details'],
      },
      {
        subtitle: 'b. Customer data (your end users)',
        list: [
          'Messages exchanged via chatbot (WhatsApp / website)',
          'Contact details (if shared during conversations)',
          'Interaction history',
        ],
      },
      {
        subtitle: 'c. Technical data',
        list: ['IP address', 'Device / browser type', 'Usage logs', 'API requests'],
      },
    ],
  },
  {
    title: 'How We Use Data',
    content: [
      {
        text: 'We use collected data to:',
        list: [
          'Provide and operate the Service',
          'Improve chatbot performance',
          'Enable AI training (only if enabled by you)',
          'Provide customer support',
          'Ensure security and prevent misuse',
        ],
      },
    ],
  },
  {
    title: 'AI Training',
    content: [
      {
        list: [
          'AI training on your data is mandatory',
          'Your data may be used to improve chatbot responses',
          'We do not use your data to train models for other clients',
        ],
      },
    ],
  },
  {
    title: 'Data Sharing',
    content: [
      {
        highlight: 'We do not sell your data.',
      },
      {
        text: 'We may share data with:',
        list: [
          'Infrastructure providers (hosting, cloud services)',
          'Messaging providers (e.g. WhatsApp via third-party APIs)',
          'Legal authorities if required by law',
        ],
      },
    ],
  },
  {
    title: 'Third-Party Services',
    content: [
      {
        list: [
          'Our Service integrates with third-party platforms (e.g. WhatsApp)',
          'Your use of such services is also governed by their policies',
          'We are not responsible for third-party data handling practices',
        ],
      },
    ],
  },
  {
    title: 'Data Retention',
    content: [
      {
        list: [
          'Data is retained as long as your account is active',
          'After account deletion, data is removed within a reasonable period (1–3 months)',
          'Backup copies may exist temporarily',
        ],
      },
    ],
  },
  {
    title: 'Data Security',
    content: [
      {
        text: 'We implement reasonable security measures, including:',
        list: ['Encryption', 'Access controls', 'Secure infrastructure'],
      },
      {
        text: 'However, no system is 100% secure.',
      },
    ],
  },
  {
    title: 'Your Rights',
    content: [
      {
        text: 'You may:',
        list: ['Access your data', 'Request correction or deletion', 'Request account deletion'],
      },
      {
        text: 'Contact us at: support@reanoai.com',
      },
    ],
  },
  {
    title: 'Updates to Policy',
    content: [
      {
        text: 'We may update this Privacy Policy from time to time. Updates will be posted on this page with a revised effective date.',
      },
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #479EC3 0%, #3B88A9 100%)', padding: '48px 0 52px' }}>
        <div className="container">
          <Link to="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontSize: 13.5, fontWeight: 500, color: 'rgba(255,255,255,0.8)',
            textDecoration: 'none', marginBottom: 28, transition: 'color .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = '#fff'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 3L5 8l5 5"/>
            </svg>
            Back to Home
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              fontSize: 11.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)', borderRadius: 100,
              padding: '4px 14px', marginBottom: 18,
            }}>
              Legal
            </div>
            <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-.03em', color: '#fff', marginBottom: 12 }}>
              Privacy Policy
            </h1>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.78)' }}>
              Effective date: <strong style={{ color: '#fff' }}>1 April 2026</strong>&nbsp;·&nbsp;Reano AI Technologies
            </p>
          </motion.div>
        </div>
      </div>

      {/* Body */}
      <div className="container" style={{ padding: '64px 28px 96px' }}>
        <div style={{ maxWidth: 780 }}>

          {sections.map((sec, i) => (
            <motion.div
              key={sec.title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1 + i * 0.04 }}
              style={{
                background: '#fff', borderRadius: 16, padding: '32px',
                border: '1px solid #E2E8F0', marginBottom: 16,
              }}
            >
              <h2 style={{
                fontSize: 17, fontWeight: 700, color: '#0F2335',
                letterSpacing: '-.02em', marginBottom: 20,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: 'rgba(71,158,195,0.1)', color: '#479EC3',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800, flexShrink: 0,
                }}>{String(i + 1).padStart(2, '0')}</span>
                {sec.title}
              </h2>

              {sec.content.map((block, j) => (
                <div key={j} style={{ marginBottom: j < sec.content.length - 1 ? 16 : 0 }}>
                  {block.subtitle && (
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#479EC3', marginBottom: 8 }}>
                      {block.subtitle}
                    </div>
                  )}
                  {block.highlight && (
                    <p style={{ fontSize: 14.5, fontWeight: 600, color: '#0F2335', marginBottom: 8 }}>
                      {block.highlight}
                    </p>
                  )}
                  {block.text && (
                    <p style={{ fontSize: 14.5, color: '#4A6070', lineHeight: 1.75, marginBottom: block.list ? 12 : 0 }}>
                      {block.text}
                    </p>
                  )}
                  {block.list && (
                    <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {block.list.map((item, k) => (
                        <li key={k} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14.5, color: '#4A6070', lineHeight: 1.65 }}>
                          <span style={{
                            width: 6, height: 6, borderRadius: '50%', background: '#479EC3',
                            flexShrink: 0, marginTop: 8,
                          }}/>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
